import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'redux-scaffolding-ts';
import {
  DeliveryTypeDto,
  DeliveryTypesStore,
  DropDownDeliveryTypesStore
} from '../../stores/configuration/events-n-requests/delivery-types-store';
import { nameof } from '../../utils/object';
import { ItemReference } from 'stores/dataStore';
import { isNullOrWhiteSpaces } from '../../utils/useful-functions';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useTranslation } from 'react-i18next';

interface DeliveryMethodEditorProps {
  value: ItemReference;
  onChange?: (value: ItemReference) => void;
  deliveryMethods?: DeliveryTypesStore;
  placeholder?: string;
  clearable?: boolean;
  readOnly?: boolean;
  className?: string;
  nullable?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindDeliveryMethodEditor: React.FC<DeliveryMethodEditorProps> = ({
  value,
  onChange,
  deliveryMethods,
  placeholder,
  clearable = true,
  readOnly = false,
  className = '',
  nullable = false,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadDeliveryMethods = useCallback(
    async (search: string = '') => {
      try {
        setIsLoading(true);
        const result = await deliveryMethods.getAllAsync({
          searchQuery: '',
          skip: 0,
          take: 100000,
          orderBy: [{ direction: 'Ascending', field: nameof<DeliveryTypeDto>('name'), useProfile: false }],
          filter: isNullOrWhiteSpaces(search) ? [] : [`startswith(tolower(${nameof<DeliveryTypeDto>('name')}), '${search.toLowerCase()}')`]
        });

        setOptions(
          result.items.map(item => ({
            value: item.id,
            label: item.name
          }))
        );
      } catch (error) {
        console.error('Error loading locations:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [deliveryMethods]
  );

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      loadDeliveryMethods();
    }
  }, [loadDataOnOpen, options.length, loadDeliveryMethods]);

  useEffect(() => {
    if (!loadDataOnOpen || (value && options.length === 0)) {
      loadDeliveryMethods();
    }
  }, [loadDataOnOpen, value, options.length, loadDeliveryMethods]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleChange = (newValue: string | null) => {
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;

    const itemRef = selected ? { id: selected.value, title: selected.label } : null;

    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(delivery => ({
              id: delivery.value,
              text: delivery.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      searchable={!isLoading}
      disabled={readOnly}
      className="w-full"
      onOpenDropDown={onOpenDropDown}
    />
  );
};

export default connect(['deliveryMethods', DropDownDeliveryTypesStore])(TailwindDeliveryMethodEditor);
